import React, { Fragment, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import './HotTopicItem.scss';
import _ from 'lodash';
import moment from 'moment-timezone';
import { HotLabel } from '../../../AdviserHomePage/HotLabel/HotLabel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StatisticCount from './StatisticCount/StatisticCount';
import { getHotTopicItemImageUrl } from '../../../../utils/hot-topic';
import { toggleDialog } from '../../../../store/control/duck';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import StudioCanvas from '../hotTopicStudio/Editor/Canvas/StudioCanvas';
import { STUDIO_LANGUAGES } from '../hotTopicStudio/constants';
import { ensureStudioFonts } from '../hotTopicStudio/utils/fonts';

export type HotTopicItemHandle = {
  openDialog: () => void;
};

const HotTopicItem = forwardRef<HotTopicItemHandle, any>((props, ref) => {
  const {
    intl,
    itemData,
    DialogProps,
    onToggleItem,
    onItemClick,
    shouldShowStatistic,
    statistic,
    enableLanguages,
    toggleDialog,
    cardRender,
    toggleDialogShowTags,
    showStudioCover
  } = props;

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const openDialog = () => {
    toggleDialog('hotTopicItem', {
      toggleDialogShowTags: toggleDialogShowTags,
      item: itemData,
      onToggleItem,
      shouldShowStatistic,
      statistic,
      enableLanguages,
      DialogProps: {
        ...DialogProps,
        TransitionProps: {
          triggerTarget: buttonRef.current
        }
      }
    });

    if (onToggleItem) {
      onToggleItem(itemData, true);
    }
  };

  const handleClick = () => {
    if (onItemClick) {
      onItemClick(itemData);
      return;
    }
    openDialog();
  };

  const thumbnailImageUrl = getHotTopicItemImageUrl(itemData);
  // Custom lazy packs (studio) show their real first page as the cover, rendered
  // by the same StudioPage component the editor/preview use, so the thumbnail
  // matches what the adviser sees on open (classic template, Admin theme).
  // Only the custom studio picker opts in via showStudioCover; the existing lazy
  // pack list keeps its static thumbnails.
  const studioCoverPage =
    showStudioCover && itemData && Array.isArray(itemData.studioPackPages) ? itemData.studioPackPages[0] : null;
  const studioCoverSettings = {
    locale: STUDIO_LANGUAGES.some((language: any) => language.id === intl.locale) ? intl.locale : 'zh-Hant-HK',
    format: 'post',
    template: 'classic',
    themeColor: (itemData && itemData.studioPackThemeColor) || '#1456f0',
    profileName: '',
    pageCount: (itemData && Array.isArray(itemData.studioPackPages) && itemData.studioPackPages.length) || 7
  };

  useEffect(() => {
    if (studioCoverPage) {
      ensureStudioFonts();
    }
  }, [studioCoverPage]);

  const isRecentlyUpdated = itemData.updatedAt && moment(itemData.updatedAt).isAfter(moment().subtract(14, 'days'));
  const isRecentlyCreated = itemData.createdAt && moment(itemData.createdAt).isAfter(moment().subtract(14, 'days'));
  const isHot = itemData.isHot === true;
  const badge = isRecentlyUpdated
    ? intl.formatMessage({ id: 'hottopic-updated' })
    : isRecentlyCreated
    ? intl.formatMessage({ id: 'hottopic-new' })
    : isHot
    ? intl.formatMessage({ id: 'hot' })
    : undefined;

  useImperativeHandle(ref, () => ({
    openDialog
  }));

  if (cardRender) {
    return (
      <Box key={itemData.id} style={{ position: 'relative' }}>
        {cardRender(itemData, handleClick)}
      </Box>
    );
  } else
    return (
      <Fragment>
        <div style={{ position: 'relative' }}>
          {badge && <HotLabel text={badge} style={{ zIndex: 1, top: 0, left: 'unset', right: 5, borderRadius: 4 }} />}
          <Button className="hot-topic-item-button" color="primary" ref={buttonRef} onClick={handleClick}>
            <div>
              {studioCoverPage ? (
                <div className="hot-topic-studio-cover">
                  <StudioCanvas
                    className="studio-cover-canvas"
                    page={studioCoverPage}
                    settings={studioCoverSettings}
                    pageNumber={1}
                  />
                </div>
              ) : (
                <div className="hot-topic-img">
                  <img src={thumbnailImageUrl} alt="thumbnail" />
                </div>
              )}
              {shouldShowStatistic && (
                <Grid container spacing={1} style={{ paddingTop: 8 }} sx={{
                  alignItems: "center"
                }}>
                  <Grid size={6}>
                    <StatisticCount
                      IconComponent={VisibilityIcon}
                      count={_.get(statistic, 'counts["View Lazy Pack"]', 0)}
                    />
                  </Grid>
                  <Grid size={6}>
                    <StatisticCount
                      IconComponent={FavoriteIcon}
                      count={_.get(statistic, 'counts["Like Lazy Pack"]', 0)}
                    />
                  </Grid>
                </Grid>
              )}
              <div className="hot-topic-item-title">{itemData.title[intl.locale]}</div>
            </div>
          </Button>
        </div>
      </Fragment>
    );
});

const container = connect(
  (state: any) => ({
    hotTopicItemDialog: state.control.hotTopicItemDialog
  }),
  {
    toggleDialog
  },
  null,
  {
    forwardRef: true
  }
)(HotTopicItem);

export default container;
